"use client"

import React, { useCallback, useRef } from "react"
import { EntityChatTrigger } from "@gorilla-works/messages"
import { useTranslation } from "@/hooks/use-translation"
import { ClutterlessTheme } from "@/lib/message-themes"
import { emitStageMessagesChange } from "@/lib/stage-message-events"
import { useSidebarPanelObserver } from "@/hooks/use-sidebar-panel-observer"

interface MessageTriggerProps {
  entityId: string
  entityTypeId?: string
  title?: string
  size?: "sm" | "md" | "lg"
  className?: string
  tooltipText?: string
  zIndex?: number
  onMessageClick?: () => void
  onSidebarStateChange?: (isOpen: boolean) => void
  /**
   * The user's message-notifications preference. When false, opening the panel must NOT mark the
   * conversation read — that is the whole point of the switch, and the reason reading and sending
   * are gated differently: sending still absorbs your own message, so prior unread survives an off
   * period untouched.
   */
  markReadOnOpen?: boolean
}

// The message panel is a single page-wide element shared by every row's trigger, so which ENTITY it is
// currently showing is module-global, not per-instance. Tracked here so a click can tell a genuine open
// (a different entity, or none, was open) from a re-toggle of the same entity's already-open panel — the
// two must behave differently for the optimistic stage-badge zero, and a plain "is any panel open" ref
// cannot, because switching A→B without closing leaves every row reading "already open".
let openPanelEntityKey: string | null = null

export function MessageTrigger({
  entityId,
  entityTypeId = "Assignment",
  title,
  size = "sm",
  className = "",
  tooltipText,
  zIndex = 1,
  onMessageClick,
  onSidebarStateChange,
  markReadOnOpen = true,
}: MessageTriggerProps) {
  const { t, language } = useTranslation()
  const triggerRef = useRef<HTMLDivElement>(null)
  // Latest-value ref so the observer effect below can call the callback without depending on it. The grid
  // passes a fresh inline closure every render, so listing the prop in the effect's deps would tear down
  // and recreate both MutationObservers on every grid re-render, for every mounted row.
  const onSidebarStateChangeRef = useRef(onSidebarStateChange)
  onSidebarStateChangeRef.current = onSidebarStateChange

  const defaultTooltipText = tooltipText || t('viewMessages') || "View Messages"

  const handleMessageClick = useCallback(() => {
    // This handler fires on EVERY click on the trigger cell — the click that OPENS the panel, the click
    // that re-toggles THIS entity's already-open panel closed, and the click that switches the shared
    // panel from another entity to this one. Only the last two are relevant: a genuine open (this entity
    // was not the one showing) marks the thread read, so optimistically zero its stage badge; a re-toggle
    // of this same entity is a close and must NOT re-zero (it would wipe an unread the reader just set in
    // the panel, self-healing only on the next full reconcile). Compare against the page-wide open entity,
    // not a per-row "is any panel open" flag, so switching A→B still zeroes B.
    const key = `${entityTypeId}:${entityId}`
    const wasThisEntityOpen = openPanelEntityKey === key
    if (markReadOnOpen && !wasThisEntityOpen) {
      emitStageMessagesChange({ entityTypeId, entityIds: [entityId], optimisticRead: true })
    }
    openPanelEntityKey = wasThisEntityOpen ? null : key
    onMessageClick?.()
  }, [onMessageClick, markReadOnOpen, entityTypeId, entityId])

  // Watch this trigger's subtree for the page-wide message panel opening/closing and reconcile the grid.
  // The panel renders INLINE inside the opening trigger (fixed-positioned, NOT portaled to <body>), so
  // observing the subtree — not <body> — reliably catches its removal on close; a missed close left the
  // grid `overflow: unset` and froze scroll. The open/closed dedup is module-global (see
  // useSidebarPanelObserver) so an A -> B -> close B -> reopen A switch does not leave a stale per-trigger
  // cache that skips A's reopen.
  useSidebarPanelObserver(triggerRef, (isOpen) => {
    // handleMessageClick only writes openPanelEntityKey on a click of THIS trigger, so a close by any other
    // route (the panel's own close control, Escape, backdrop) would leave it stale; the observer sees the
    // real close, so clear the key here.
    if (!isOpen) openPanelEntityKey = null

    onSidebarStateChangeRef.current?.(isOpen)

    // Clear the row z-index the grid raised while the panel was open (its isMessageSidebarOpen mode).
    if (!isOpen && triggerRef.current) {
      const tableContainer = triggerRef.current.closest('table')
      if (tableContainer) {
        tableContainer.querySelectorAll('tbody tr').forEach(row => {
          if (row instanceof HTMLElement) {
            row.style.setProperty('z-index', '', 'important')
          }
        })
      }
    }
  })

  // No wrapping Tooltip any more.
  //
  // The package renders its own hover card on this cell — who wrote the newest message, when, and a
  // three-line snippet — and a generic "View Messages" tooltip on top of it meant two hover surfaces
  // fighting for the same 68px target, with the less useful one winning because it sat on the outer
  // element. Rows with nothing to preview are covered by the package's own `title` ("No messages").
  // `aria-label` stays here, so the accessible name is unchanged.
  return (
          <div
            ref={triggerRef}
            className="flex items-center justify-center"
            aria-label={defaultTooltipText}
            style={{ position: 'relative', zIndex }}
            onClick={handleMessageClick}
          >
            <EntityChatTrigger
              entityTypeId={entityTypeId}
              entityId={entityId}
              title={title}
              variant="icon"
              size={size}
              className={className}
              // Follows the app's language, which comes from the token. Hardcoding "en" here served
              // French tenants an English panel inside an otherwise translated grid.
              language={language}
              panelPosition="right"
              slideOrigin="right"
              // One batched conversations/summaries call for the whole visible page instead of a
              // message-count call per row, and the badge becomes THIS user's unread count rather
              // than the thread total — a total puts a number on every row and hides the one that
              // needs attention.
              //
              // Only rows whose entity type the conversation migration has run for show a badge at
              // all: unread cannot exist before a conversation document does.
              //
              // Notifications off → 'hidden': still FETCHES the summary so a thread that has messages
              // shows the quiet neutral "read" bubble — a participant must still be able to see and open
              // their conversations — but withholds every urgency signal (no unread count, no "!", no
              // badge). 'none' suppressed the fetch entirely and blanked every icon, which hid the
              // conversations the reader is part of; 'off' would put a per-row total back, which is the
              // number we do not want.
              summaryMode={markReadOnOpen ? 'unread' : 'hidden'}
              markReadOnOpen={markReadOnOpen}
              sx={ClutterlessTheme}
            />
          </div>
  )
}
